'use client';

import { useState } from 'react';
import { useSession } from 'next-auth/react';
import './RatingForm.scss';

interface RatingFormProps {
  sketchId: string;
  currentControlSettings?: Record<string, number>;
  existingRating?: {
    id: string;
    stars: number;
    worked: boolean;
    printerInfo?: string;
    filamentInfo?: string;
    notes?: string;
  };
  onRatingSubmitted?: () => void;
}

export default function RatingForm({
  sketchId,
  currentControlSettings = {},
  existingRating,
  onRatingSubmitted,
}: RatingFormProps) {
  const { data: session, status } = useSession();
  const [stars, setStars] = useState<number>(existingRating?.stars || 2);
  const [worked, setWorked] = useState<boolean>(existingRating?.worked ?? true);
  const [printerInfo, setPrinterInfo] = useState<string>(existingRating?.printerInfo || '');
  const [filamentInfo, setFilamentInfo] = useState<string>(existingRating?.filamentInfo || '');
  const [notes, setNotes] = useState<string>(existingRating?.notes || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!session) {
      setError('You must be logged in to submit a rating');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`/api/sketches/${sketchId}/ratings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          stars,
          worked,
          printerInfo: printerInfo.trim() || undefined,
          filamentInfo: filamentInfo.trim() || undefined,
          controlSettings: Object.keys(currentControlSettings).length > 0 
            ? currentControlSettings 
            : undefined,
          notes: notes.trim() || undefined,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setIsExpanded(false);
        onRatingSubmitted?.();
      } else {
        setError(data.error || 'Failed to submit rating');
      }
    } catch (err) {
      console.error('Error submitting rating:', err);
      setError('Failed to submit rating');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (status === 'loading') {
    return <div className="rating-form loading">Loading...</div>;
  }

  if (!session) {
    return (
      <div className="rating-form not-logged-in">
        <p>Please sign in to rate this sketch</p>
      </div>
    );
  }

  return (
    <div className="rating-form">
      <div className="rating-form-header">
        <h4>Rate This Print</h4>
        <button
          type="button"
          className="toggle-btn"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? '▼' : '▶'}
        </button>
      </div>

      {isExpanded && (
        <form onSubmit={handleSubmit} className="rating-form-content">
          {error && <div className="error-message">{error}</div>}

          {/* Star Rating */}
          <div className="form-group">
            <label>Quality Rating</label>
            <div className="star-selector">
              {[1, 2, 3].map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`star-btn ${stars >= value ? 'active' : ''}`}
                  onClick={() => setStars(value)}
                  aria-label={`${value} star${value > 1 ? 's' : ''}`}
                >
                  ★
                </button>
              ))}
            </div>
            <span className="star-label">
              {stars === 1 ? 'Needs Work' : stars === 2 ? 'Good' : 'Excellent'}
            </span>
          </div>

          {/* Did it work? */}
          <div className="form-group">
            <label>Did the print succeed?</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  checked={worked === true}
                  onChange={() => setWorked(true)}
                />
                <span>Yes, it worked!</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  checked={worked === false}
                  onChange={() => setWorked(false)}
                />
                <span>No, it failed</span>
              </label>
            </div>
          </div>

          {/* Printer Info */}
          <div className="form-group">
            <label htmlFor="printer-info">Printer (optional)</label>
            <input
              id="printer-info"
              type="text"
              value={printerInfo}
              onChange={(e) => setPrinterInfo(e.target.value)}
              placeholder="e.g., Prusa i3 MK3S+"
              maxLength={100}
            />
          </div>

          {/* Filament Info */}
          <div className="form-group">
            <label htmlFor="filament-info">Filament (optional)</label>
            <input
              id="filament-info"
              type="text"
              value={filamentInfo}
              onChange={(e) => setFilamentInfo(e.target.value)}
              placeholder="e.g., PLA, 0.4mm nozzle"
              maxLength={100}
            />
          </div>

          {/* Control Settings Display */}
          {Object.keys(currentControlSettings).length > 0 && (
            <div className="form-group">
              <label>Control Settings Used</label>
              <div className="control-settings-display">
                {Object.entries(currentControlSettings).map(([name, value]) => (
                  <div key={name} className="control-setting-item">
                    <span className="setting-name">{name}:</span>
                    <span className="setting-value">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes">Notes (optional)</label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional comments about your print..."
              rows={3}
              maxLength={500}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : existingRating ? 'Update Rating' : 'Submit Rating'}
          </button>
        </form>
      )}
    </div>
  );
}
